import React from 'react';
import {ScrollView, StyleSheet, Text, View} from 'react-native';
import {storiesOf} from '@storybook/react-native';

import FormField from '/component/base/FormField';
import {
  Code,
  FlexView,
  Heading,
  Spacer,
  Spacing,
} from '/component/base/Storybook';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';

const styles = StyleSheet.create({
  fieldContainer: {
    width: '100%',
  },
});

storiesOf('base', module).add('FormField', () => (
  // @ts-expect-error TS(2322): Type '{ children: Element; isStorybook: boolean; }... Remove this comment to see the full error message
  <KeyboardAvoidingView isStorybook={true}>
    <ScrollView>
      <Spacing size={20}>
        <FlexView>
          <Heading level={1}>FormField</Heading>
          <Spacer size={24} />
          <Text>
            The <Code>FormField</Code> component renders a{' '}
            <Code>TextInput</Code> component with a underline and changes color
            when the <Code>TextInput</Code> is focused.
          </Text>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField value="Value" />
          </View>
          <Spacer size={16} />
          <Text>
            All examples in this story are "controlled components" meaning since
            the <Code>value</Code> prop is specified, attempting to change the
            field's value will fail.
          </Text>
          <Spacer size={16} />
          <Text>
            The only exceptions to "controlled components" are the examples with
            only the <Code>placeholder</Code> prop and in those cases the
            components are "uncontrolled component" and attempting to change the
            field's value should succeed.
          </Text>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField placeholder="Placeholder" />
          </View>
          <Spacer size={16} />
          <Text>
            You can override the styles of the Formfield when it's focused by
            passing custom <Code>style</Code> prop like{' '}
            <Code>focused: backgroundColor: 'red'</Code>
          </Text>
          <View style={styles.fieldContainer}>
            <FormField
              value="Overriding styles when FormField in focused"
              style={{focused: {backgroundColor: 'red'}}}
            />
          </View>
          <Spacer size={24} />
          <Heading level={2}>FormField with Label</Heading>
          <Spacer size={16} />
          <Text>
            The <Code>FormField</Code> can optionally render a label from the
            given <Code>label</Code> prop.
          </Text>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField placeholder="Placeholder with label" label="Label" />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField value="Value with label" label="Label" />
          </View>
          <Spacer size={24} />
          <Heading level={2}>FormField with Assistive Text</Heading>
          <Spacer size={16} />
          <Text>
            The <Code>FormField</Code> can optionally render assistive text from
            the given <Code>assistiveText</Code> prop.
          </Text>
          <Spacer size={16} />
          <Text>The Assistive text is rendered below the divider element.</Text>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              placeholder="Placeholder with assistive text"
              assistiveText="Assistive text"
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with assistive text"
              assistiveText="Assistive text"
            />
          </View>
          <Spacer size={24} />
          <Heading level={2}>FormField with Icon</Heading>
          <Spacer size={16} />
          <Text>
            The <Code>FormField</Code> can optionally render a icon from the
            given <Code>icon</Code> prop.
          </Text>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField placeholder="Placeholder with icon" icon="dashboard" />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField value="Value with icon" icon="profile" />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon and assistive text"
              icon="team"
              assistiveText="Assistive text"
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon and label"
              icon="phone"
              label="Label"
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon, label and assistive text"
              icon="settings"
              label="Label"
              assistiveText="Assistive text"
            />
          </View>
          <Spacer size={16} />
          <Spacer size={24} />
          <Heading level={2}>FormField with Error</Heading>
          <Spacer size={16} />
          <Text>
            When the <Code>hasError</Code> prop is set to <Code>true</Code>, the
            color for the divider element, the optional assistive text element
            and the optional icon element are rendered in a different color to
            indicate the presence of error.
          </Text>
          <Spacer size={16} />
          <Text>
            The error color for the divider element also overrides the focused
            color for the divider as well.
          </Text>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField placeholder="Placeholder with error" hasError={true} />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField value="Value with error" hasError={true} />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with assistive text and error"
              assistiveText="Assistive text"
              hasError={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with label and error"
              label="Label"
              hasError={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with label, assistive text and error"
              label="Label"
              assistiveText="Assistive text"
              hasError={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              placeholder="Placeholder with icon and error"
              icon="dashboard"
              hasError={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon and error"
              icon="profile"
              hasError={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon, assistive text and error"
              icon="team"
              assistiveText="Assistive text"
              hasError={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon, label and error"
              icon="phone"
              label="Label"
              hasError={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon, label, assistive text and error"
              icon="settings"
              label="Label"
              assistiveText="Assistive text"
              hasError={true}
            />
          </View>
          <Spacer size={24} />
          <Heading level={2}>FormField in Disabled State</Heading>
          <Spacer size={16} />
          <Text>
            When the <Code>disabled</Code> prop is set to <Code>true</Code>, the{' '}
            <Code>TextInput</Code> component inside <Code>FormField</Code> is
            disabled. In addition, other elements has a different style to
            indicate the disabled state of the overall component.
          </Text>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              placeholder="Placeholder in disabled state"
              disabled={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField value="Value in disabled state" disabled={true} />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with assistive text in disabled state"
              assistiveText="Assistive text"
              disabled={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with label in disabled state"
              label="Label"
              disabled={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with label and assistive text in disabled state"
              label="Label"
              assistiveText="Assistive text"
              disabled={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon in disabled state"
              icon="profile"
              disabled={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon and assistive text in disabled state"
              icon="team"
              assistiveText="Assistive text"
              disabled={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon and label in disabled state"
              icon="phone"
              label="Label"
              disabled={true}
            />
          </View>
          <Spacer size={16} />
          <View style={styles.fieldContainer}>
            <FormField
              value="Value with icon, label and assistive text in disabled state"
              icon="settings"
              label="Label"
              assistiveText="Assistive text"
              disabled={true}
            />
          </View>
        </FlexView>
      </Spacing>
    </ScrollView>
  </KeyboardAvoidingView>
));
